import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Services", to: "/#services", id: "services" },
  { label: "Projects", to: "/#projects", id: "projects" },
  { label: "About", to: "/#about", id: "about" },
  { label: "Process", to: "/#process", id: "process" },
  { label: "FAQ", to: "/#faq", id: "faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-warm/95 backdrop-blur-md transition-shadow duration-300 ${
        scrolled || open ? "shadow-[0_1px_0_rgba(23,23,23,0.1)]" : ""
      }`}
    >
      <div className="container-cando flex h-20 items-center justify-between">
        <Link to="/" data-testid="site-logo" className="leading-none">
          <span className="block font-heading text-[22px] font-800 font-extrabold tracking-[0.08em] text-charcoal">
            CANDO
          </span>
          <span className="block font-heading text-[10px] font-bold tracking-[0.42em] text-mute">
            CONSTRUCTION
          </span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <Link
              key={item.id}
              to={item.to}
              data-testid={`nav-link-${item.id}`}
              className="font-heading text-[15px] font-semibold text-ink transition-colors duration-300 hover:text-bronze"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="header-quote-button" className="btn btn-primary !py-3.5 !px-7">
            Get a Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="flex h-11 w-11 items-center justify-center text-charcoal lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-charcoal/10 bg-warm lg:hidden"
          >
            <div className="container-cando flex flex-col gap-1 py-6">
              {NAV.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  data-testid={`mobile-nav-link-${item.id}`}
                  className="py-3 font-heading text-lg font-semibold text-ink"
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/contact" data-testid="mobile-quote-button" className="btn btn-primary mt-4 w-full">
                Get a Quote
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
